import React from 'react';
import type { LegalBlock } from '../types/legal';
interface LegalDocumentProps {
  blocks: LegalBlock[];
  /** Fixed anchor IDs for specific headings, e.g. existing deep links. */
  idOverrides?: Record<string, string>;
}
interface Section {
  heading: string;
  id: string;
  blocks: LegalBlock[];
}
const LINK_CLASS = 'font-bold text-ink underline decoration-coral decoration-2 underline-offset-4 [overflow-wrap:anywhere] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink';

// Emails, https URLs and the one US phone format used in the approved text.
const TOKEN = /(https?:\/\/[^\s]+|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}|\(\d{3}\) \d{3}-\d{4})/g;

/** Renders approved legal paragraphs with a contents list, h2 sections and real links. */
export function LegalDocument({
  blocks,
  idOverrides = {}
}: LegalDocumentProps) {
  const {
    intro,
    sections
  } = groupBlocks(blocks, idOverrides);
  return <section className="bg-cream" data-id="element-247" data-mp="3q6rfwtamb8vv">
      <div className="mx-auto max-w-[760px] px-5 py-14 sm:px-6 md:py-20" data-id="element-248" data-mp="2l4gnta3inzu9">
        <div className="space-y-5 text-[18px] leading-[1.7] text-ink" data-id="element-249" data-mp="2q4309hbitc20">{renderBlocks(intro)}</div>

        <nav aria-label="Contents" className="mt-10 border-y border-ink/15 py-6" data-id="element-250" data-mp="2c4bgt128j3gm">
          <h2 className="font-display text-[22px] text-ink" data-id="element-251" data-mp="2c6eiiicr2l16">Contents</h2>
          <ol className="mt-3 grid gap-x-8 gap-y-1 sm:grid-cols-2" data-id="element-252" data-mp="2g6f5wshtu6kd">
            {sections.map(section => <li key={section.id} data-id="element-253" data-mp="33w9gf3eobj1g">
                <a href={`#${section.id}`} className="inline-flex min-h-[44px] items-center py-1 text-[17px] font-semibold text-ink underline decoration-ink/25 decoration-2 underline-offset-4 transition-colors duration-150 ease-out hover:decoration-coral focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink" data-id="element-254" data-mp="38ub09qjxbmdz">
                  {section.heading}
                </a>
              </li>)}
          </ol>
        </nav>

        {sections.map(section => <section key={section.id} aria-labelledby={section.id} className="mt-12" data-id="element-255" data-mp="59k97kj8sv25">
            <h2 id={section.id} className="scroll-mt-28 font-display text-[28px] leading-tight text-ink md:text-[32px]" data-id="element-256" data-mp="1akuj87pljxi">
              {section.heading}
            </h2>
            <div className="mt-4 space-y-5 text-[18px] leading-[1.7] text-ink" data-id="element-257" data-mp="3ni2lzn0jzdty">{renderBlocks(section.blocks)}</div>
          </section>)}
      </div>
    </section>;
}
function groupBlocks(blocks: LegalBlock[], idOverrides: Record<string, string>) {
  const intro: LegalBlock[] = [];
  const sections: Section[] = [];
  for (const block of blocks) {
    if (block.style === 'Title') continue;
    if (block.style === 'Heading 1') {
      sections.push({
        heading: block.text,
        id: idOverrides[block.text] ?? slugify(block.text),
        blocks: []
      });
    } else if (sections.length === 0) {
      intro.push(block);
    } else {
      sections[sections.length - 1].blocks.push(block);
    }
  }
  return {
    intro,
    sections
  };
}
function renderBlocks(blocks: LegalBlock[]) {
  const out: React.ReactNode[] = [];
  let list: LegalBlock[] = [];
  const flush = () => {
    if (list.length === 0) return;
    out.push(<ul key={`ul-${out.length}`} className="list-disc space-y-3 pl-6 marker:text-coral" data-id="element-258" data-mp="2unweugiirt2z">
        {list.map((item, i) => <li key={i} className="pl-1" data-id="element-259" data-mp="2zl6dmmrsth3h" data-mpi={i}>
            {linkify(item.text)}
          </li>)}
      </ul>);
    list = [];
  };
  blocks.forEach((block, i) => {
    if (block.style === 'List Bullet') {
      list.push(block);
      return;
    }
    flush();
    if (block.text.includes('\n')) {
      const lines = block.text.split('\n');
      out.push(<address key={i} className="rounded-2xl bg-cream-field px-5 py-4 not-italic" data-id="element-260" data-mp="2gnbw1kcltx9h">
          {lines.map((line, j) => <React.Fragment key={j} data-id="element-261" data-mp="1j6wovrjn9qc4" data-mpi={j}>
              {linkify(line)}
              {j < lines.length - 1 && <br data-id="element-262" data-mp="1o5hq9uamug25" />}
            </React.Fragment>)}
        </address>);
    } else {
      out.push(<p key={i} data-id="element-263" data-mp="3pwq6eqfcul09">{linkify(block.text)}</p>);
    }
  });
  flush();
  return out;
}
function linkify(text: string): React.ReactNode[] {
  const parts = text.split(TOKEN);
  return parts.map((part, i) => {
    if (i % 2 === 0) return part;
    // Keep sentence punctuation outside the link.
    const match = part.match(/^(.*?)([.,;:)]*)$/);
    const core = match ? match[1] : part;
    const trailing = match ? match[2] : '';
    let href: string;
    if (core.startsWith('http')) href = core;else if (core.includes('@')) href = `mailto:${core}`;else href = `tel:+1${core.replace(/\D/g, '')}`;
    const external = core.startsWith('http');
    return <React.Fragment key={i} data-id="element-264" data-mp="hgen0w3uvx2b" data-mpi={i}>
        <a href={href} className={LINK_CLASS} {...external ? {
        target: '_blank',
        rel: 'noopener noreferrer'
      } : {}} data-id="element-265" data-mp="hh6je95a1ytn">
          {core}
        </a>
        {trailing}
      </React.Fragment>;
  });
}
function slugify(text: string) {
  return text.toLowerCase().replace(/[’']/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}